import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useState } from 'react';

/**
 * One object per mounted mutation, used only for its identity: it goes into the
 * mutation's `meta`, which the global failure handler can read, and into the
 * set below while a call is being awaited.
 */
export type ErrorOwnershipToken = Record<string, never>;

export interface WriteMutationMeta extends Record<string, unknown> {
  errorOwnershipToken: ErrorOwnershipToken;
}

/**
 * The mutations whose caller is currently awaiting them, and has therefore
 * taken responsibility for reporting a failure.
 *
 * A module-level WeakSet rather than a flag on the token, because the token is
 * React state and state is not ours to mutate. Weak so a component that
 * unmounts mid-call takes its entry with it.
 */
const awaitedByCaller = new WeakSet<ErrorOwnershipToken>();

/** Whether the call site is going to report this failure itself. */
export function callerReportsErrors(meta: unknown): boolean {
  const token = (meta as WriteMutationMeta | undefined)?.errorOwnershipToken;

  return token !== undefined && awaitedByCaller.has(token);
}

/**
 * `useMutation`, with an answer to "who reports this when it fails".
 *
 * About forty mutations hand-wrote the same `onError` toast, and the ones that
 * forgot were the worst of them: approving an extraction, clocking in from the
 * scan dialog, saving a notification preference — all of which failed in
 * silence, leaving somebody to press the button again. A global handler fixes
 * that class of bug rather than the instances, but on its own it would also
 * toast over the forms that already show the failure under the field.
 *
 * The rule that separates them is the call style, which is exactly the
 * distinction being made anyway: **`mutateAsync` means the caller awaited the
 * result and owns what happens next** — that is the only reason to reach for
 * it — while `mutate` is fire-and-forget and has nobody to catch anything. So
 * awaiting silences the global toast, and the flag is cleared once the call has
 * settled so the same hook can be used both ways.
 */
export function useWriteMutation<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  // State rather than a ref, holding one object for the life of the component:
  // it is read while rendering, to build `meta`, which a ref is not for.
  const [token] = useState<ErrorOwnershipToken>(() => ({}));

  const mutation = useMutation<TData, TError, TVariables, TContext>({
    ...options,
    meta: { ...options.meta, errorOwnershipToken: token } satisfies WriteMutationMeta,
  });

  return {
    ...mutation,
    mutateAsync: (variables, mutateOptions) => {
      awaitedByCaller.add(token);

      // Cleared after the call settles, which is after the cache's handlers
      // have run and read it — so one awaited call does not silence the next
      // fire-and-forget one from the same hook.
      return mutation.mutateAsync(variables, mutateOptions).finally(() => {
        awaitedByCaller.delete(token);
      });
    },
  };
}
